import { useCallback, useEffect, useRef, useState } from 'react';

/** Espera desde el último cambio antes de guardar solo (SPEC: 2 s). */
export const DEMORA_AUTOGUARDADO_MS = 2000;

/**
 * La espera que se usa cuando quien llama no da otra. Es un objeto y no una
 * constante sólo para que las pruebas que NO son del autoguardado la alarguen
 * (con temporizadores reales, un guardado a los 2 s se colaba a media prueba).
 */
export const AUTOGUARDADO = { demoraMs: DEMORA_AUTOGUARDADO_MS };

interface Opciones {
  /**
   * Se puede guardar solo AHORA: estado editable (sin captura, borrador o
   * rechazada), permiso de capturar, sin errores de celda, BUK arriba y sin un
   * fallo que lo haya detenido (409, 422, 503). Lo decide quien lo usa.
   */
  habilitado: boolean;
  /** Hay algo sin guardar. */
  sucio: boolean;
  /** Cambia con cada edición (las filas, la nota): reinicia la espera. */
  firma: unknown;
  /**
   * Guarda lo que haya. `true` si quedó guardado (o no había nada), `false` si
   * no se pudo. Quien lo implementa ya pone un guardado detrás de otro.
   */
  guardar: () => Promise<boolean>;
  /** Lo mismo que `habilitado && sucio`, pero leído en el momento (tras un `await`). */
  pendienteAhora: () => { habilitado: boolean; sucio: boolean };
  demoraMs?: number;
}

export interface Autoguardado {
  /** Hay un guardado esperando sus 2 s. */
  programado: boolean;
  /** Hay un guardado automático en camino. */
  guardando: boolean;
  /**
   * Guarda YA lo pendiente —antes de cambiar de tienda o de semana, o al
   * enviar—: cancela la espera, deja terminar el que va en camino y, si sigue
   * habiendo algo, lo guarda. `true` = no queda nada sin guardar; `false` = hay
   * cambios que no se pudieron guardar (no está habilitado o falló).
   */
  guardarYa: () => Promise<boolean>;
  /** Cancela la espera (p. ej. al descartar los cambios). */
  cancelar: () => void;
}

/**
 * Guardado automático de la captura (fase 2b, decisión del 29-sep):
 *
 * - espera `demoraMs` (2 s) desde el ÚLTIMO cambio;
 * - nunca manda dos guardados a la vez: si al cumplirse la espera ya va uno en
 *   camino, guarda otra vez cuando termine (lo que se escribió mientras tanto);
 * - sólo cuando `habilitado`: quien lo usa lo apaga en enviada y autorizada,
 *   sin permiso, con errores de celda o tras un 409/422/503;
 * - al ocultarse la página (cambiar de pestaña del navegador, bloquear el
 *   teléfono) guarda lo pendiente sin esperar.
 */
export function useAutoguardado({
  habilitado,
  sucio,
  firma,
  guardar,
  pendienteAhora,
  demoraMs = AUTOGUARDADO.demoraMs,
}: Opciones): Autoguardado {
  const [programado, setProgramado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enVuelo = useRef<Promise<boolean> | null>(null);
  const otraVez = useRef(false);
  const vivo = useRef(true);

  // Lo de este render, para lo que corre después de un `await` o de un temporizador.
  const guardarRef = useRef(guardar);
  guardarRef.current = guardar;
  const pendienteRef = useRef(pendienteAhora);
  pendienteRef.current = pendienteAhora;

  const limpiar = useCallback(() => {
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = null;
    if (vivo.current) setProgramado(false);
  }, []);

  const disparar = useCallback(async (): Promise<boolean> => {
    limpiar();
    if (enVuelo.current) {
      otraVez.current = true;
      return enVuelo.current;
    }
    const ahora = pendienteRef.current();
    if (!ahora.sucio) return true;
    if (!ahora.habilitado) return false;
    const p = (async () => {
      if (vivo.current) setGuardando(true);
      try {
        return await guardarRef.current();
      } finally {
        enVuelo.current = null;
        if (vivo.current) setGuardando(false);
      }
    })();
    enVuelo.current = p;
    const ok = await p;
    // Lo que se escribió mientras iba el guardado: otro, en cuanto terminó.
    if (otraVez.current && vivo.current) {
      otraVez.current = false;
      const despues = pendienteRef.current();
      if (ok && despues.sucio && despues.habilitado) return disparar();
    }
    return ok;
  }, [limpiar]);

  // Cada cambio reinicia la espera.
  useEffect(() => {
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = null;
    if (!habilitado || !sucio) {
      setProgramado(false);
      return;
    }
    setProgramado(true);
    temporizador.current = setTimeout(() => {
      temporizador.current = null;
      void disparar();
    }, demoraMs);
  }, [firma, habilitado, sucio, demoraMs, disparar]);

  // Al ocultarse la página, lo pendiente se guarda sin esperar.
  useEffect(() => {
    const alCambiar = () => {
      if (document.visibilityState === 'hidden' && temporizador.current) void disparar();
    };
    document.addEventListener('visibilitychange', alCambiar);
    return () => document.removeEventListener('visibilitychange', alCambiar);
  }, [disparar]);

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
      if (temporizador.current) clearTimeout(temporizador.current);
      temporizador.current = null;
    };
  }, []);

  const guardarYa = useCallback(async (): Promise<boolean> => {
    limpiar();
    if (enVuelo.current) {
      await enVuelo.current.catch(() => false);
    }
    const ahora = pendienteRef.current();
    if (!ahora.sucio) return true;
    if (!ahora.habilitado) return false;
    return disparar();
  }, [disparar, limpiar]);

  return { programado, guardando, guardarYa, cancelar: limpiar };
}

export default useAutoguardado;
